<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
      <h2 class="text-lg font-bold mb-4">Подтверждение комплектования</h2>
      
      <!-- Информация о продукте -->
      <div class="mb-4 p-3 bg-neutral-50 rounded-lg">
        <h3 class="font-medium text-neutral-900">{{ product.product_name }}</h3>
        
        <!-- Для штучной продукции -->
        <div v-if="product.is_single" class="text-sm text-neutral-600 mt-1">
          <div v-if="product.batch_info?.batch_number || product.batch_number">
            Партия: {{ product.batch_info?.batch_number || product.batch_number }}
          </div>
          <div v-if="product.supplier_name">
            Поставщик: {{ product.supplier_name }}
          </div>
          <div v-if="product.serial_number">
            SN: {{ product.serial_number }}
          </div>
        </div>
        
        <!-- Для количественной продукции -->
        <div v-else class="text-sm text-neutral-600 mt-1">
          <div v-if="product.is_package">
            Упаковка №{{ product.storehouse_product_id }}
          </div>
        </div>
      </div>

      <!-- Выбор места для количественной продукции (если мест > 1) -->
      <div v-if="!product.is_single && product.placements && product.placements.length > 1" class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Выберите место
        </label>
        <select
          v-model="selectedPlacement"
          @change="onPlacementChange"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option :value="null" disabled>Выберите место...</option>
          <option v-for="placement in product.placements" :key="placement.storehouse_product_id" :value="placement">
            {{ formatPlacementLabel(placement) }}
          </option>
        </select>
        
        <!-- Информация о выборе места -->
        <div v-if="product.placements && product.placements.length > 1" class="mt-2">
          <!-- Красное сообщение если выбрано невалидное место -->
          <div v-if="selectedPlacement && !isPlacementValid" class="p-2 bg-red-50 border border-red-200 rounded">
            <p class="text-sm text-red-800">
              Выбранное место отсутствует в инструкциях по комплектованию
            </p>
          </div>
          <!-- Оранжевое информационное сообщение если место валидное или не выбрано -->
          <div v-else class="p-2 bg-orange-50 border border-orange-200 rounded">
            <p class="text-sm text-orange-800">
              Была отсканирована этикетка входного контроля, необходимо указать точное место взятия продукции
            </p>
          </div>
        </div>
      </div>

      <!-- Ввод количества для количественной продукции -->
      <div v-if="!product.is_single" class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Количество (доступно: {{ formatQuantity(computedAvailableQuantity) }})
        </label>
        <input
          ref="quantityInput"
          v-model.number="quantity"
          type="number"
          min="0.001"
          :max="computedAvailableQuantity"
          :disabled="!product.is_single && product.placements && product.placements.length > 1 && !selectedPlacement"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        
        <!-- Потребность закрыта: количество предлагать нечего, вводит комплектовщик -->
        <p v-if="remainingQuantity <= 0" class="mt-2 text-sm text-neutral-600">
          Потребность по этой позиции закрыта. Укажите количество, если берёте сверх плана.
        </p>

        <!-- Предупреждение если место одно но невалидное -->
        <div v-if="product.placements && product.placements.length === 1 && !isPlacementValid" class="mt-2 p-2 bg-red-50 border border-red-200 rounded">
          <p class="text-sm text-red-800">
            Место размещения отсутствует в инструкциях по комплектованию
          </p>
        </div>
      </div>

      <!-- Комплектование идёт строго по заданию: взять больше нормы нельзя (Z-21).
           Раньше здесь было окно подтверждения перерасхода -->
      <div v-if="isOverpick" class="mb-4 p-3 rounded-lg border bg-red-50 border-red-300">
        <p class="text-sm font-medium text-red-800">
          Больше, чем требуется по заданию, взять нельзя
        </p>
        <p class="text-sm mt-1 text-red-800">
          {{ overpickMessage }}
        </p>
        <p class="text-sm mt-2 text-red-800">
          Уменьшите количество до нормы по заданию.
        </p>
      </div>

      <!-- Кнопки -->
      <div class="flex justify-end space-x-2">
        <button
          type="button"
          @click="closeModal"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Отмена
        </button>
        <button
          @click="confirmPicking"
          :disabled="!canConfirm"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Подтвердить
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PickingModal',
  props: {
    product: {
      type: Object,
      required: true
    },
    availableQuantity: {
      type: Number,
      required: true
    },
    isAlreadyEnough: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      quantity: 1,
      selectedPlacement: null,
    }
  },
  computed: {
    requiredQuantity() {
      return Number(this.product.required_quantity || 0)
    },

    pickedQuantity() {
      return Number(this.product.picked_count || 0)
    },

    // Сколько ещё нужно по спецификации. Может быть нулём или меньше — потребность закрыта
    remainingQuantity() {
      return Math.round((this.requiredQuantity - this.pickedQuantity) * 1000) / 1000
    },

    enteredQuantity() {
      const value = Number(this.quantity)
      return isNaN(value) ? 0 : value
    },

    overpickQuantity() {
      return Math.round((this.enteredQuantity - this.remainingQuantity) * 1000) / 1000
    },

    // Перерасход считается по локальному состоянию — окончательное решение за сервером,
    // здесь окно нужно, чтобы взятие сверх плана не прошло незаметно
    isOverpick() {
      if (this.enteredQuantity <= 0) return false

      if (this.requiredQuantity > 0) {
        return this.overpickQuantity > 0.0005
      }

      return this.isAlreadyEnough
    },

    overpickMessage() {
      return `Требуется ${this.formatQuantity(this.requiredQuantity)}, уже взято ${this.formatQuantity(this.pickedQuantity)},`
        + ` вы берёте ${this.formatQuantity(this.enteredQuantity)} — перерасход ${this.formatQuantity(Math.max(this.overpickQuantity, 0))}`
        + ` ${this.getUnitLabel(this.product)}`
    },

    computedAvailableQuantity() {
      // Для штучной - всегда из props
      if (this.product.is_single) {
        return this.availableQuantity
      }
      
      // Для количественной - из выбранного места
      if (this.selectedPlacement) {
        return this.selectedPlacement.count
      }
      
      // Если мест несколько и ничего не выбрано - 0
      if (this.product.placements && this.product.placements.length > 1) {
        return 0
      }
      
      // Если место одно - из props
      return this.availableQuantity
    },
    
    isPlacementValid() {
      // Для штучной всегда валидно
      if (this.product.is_single) {
        return true
      }
      
      // Если место не выбрано - считаем невалидным
      if (!this.selectedPlacement) {
        return false
      }
      
      // Потребность закрыта — мест взятия в инструкции не осталось, отбраковывать нечем:
      // взятие сверх плана подтверждается отдельно
      const validIds = this.product.valid_location_ids || []
      if (validIds.length === 0) {
        return true
      }

      // Проверяем есть ли место в списке валидных
      return validIds.includes(this.selectedPlacement.storehouse_product_id)
    },
    
    isValidQuantity() {
      if (!this.quantity || this.quantity < 0.001) return false
      
      if (this.product.is_single) {
        return this.quantity === 1
      }
      
      // Для количественной с несколькими местами - проверяем что место выбрано
      if (this.product.placements && this.product.placements.length > 1 && !this.selectedPlacement) {
        return false
      }
      
      // Проверяем что выбранное место валидно (есть в инструкциях)
      if (!this.isPlacementValid) {
        return false
      }

      return this.quantity <= this.computedAvailableQuantity
    },

    // Перерасход запрещён строго: подтвердить количество больше нормы нельзя (Z-21)
    canConfirm() {
      if (!this.isValidQuantity) return false

      return !this.isOverpick
    }
  },

  mounted() {
    // Автовыбор если 1 место для количественной
    if (!this.product.is_single && this.product.placements && this.product.placements.length === 1) {
      this.selectedPlacement = this.product.placements[0]
    }
    
    // Устанавливаем начальное значение в зависимости от типа продукции
    if (this.product.is_single) {
      this.quantity = 1
    } else {
      this.quantity = this.suggestedQuantity(this.computedAvailableQuantity)
    }

    // Подтверждение перерасхода снимаем после предзаполнения: watch на quantity
    // срабатывает на начальном значении, а подтверждать пользователь ещё ничего не мог

    // Фокусируемся на поле ввода если количественная и место уже выбрано
    if (!this.product.is_single && (!this.product.placements || this.product.placements.length === 1)) {
      this.$nextTick(() => {
        this.$refs.quantityInput?.focus()
        this.$refs.quantityInput?.select()
      })
    }
  },
  methods: {
    formatQuantity(val) {
      if (val == null || val === undefined) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },
    getUnitLabel(p) {
      return p?.unit_short_name || p?.unit?.short_name || 'шт';
    },
    formatPlacementLabel(placement) {
      const parts = []
      
      // Путь или упаковка
      if (placement.is_package) {
        parts.push(`Упаковка №${placement.storehouse_product_id}`)
      } else {
        parts.push(placement.full_address)
      }
      
      // Количество (форматирование для избежания float-артефактов)
      parts.push(`(${this.formatQuantity(placement.count)} ${this.getUnitLabel(this.product)})`)
      
      return parts.join(' ')
    },
    
    // Подсказка по количеству: остаток потребности, но не больше того, что лежит в месте.
    // Потребность закрыта — поле остаётся пустым, количество вводит комплектовщик
    suggestedQuantity(available) {
      const remaining = this.remainingQuantity
      const inPlace = Number(available) || 0

      if (remaining <= 0 || inPlace <= 0) return null

      return Math.round(Math.min(remaining, inPlace) * 1000) / 1000
    },

    onPlacementChange() {
      // Устанавливаем количество: минимум из того что надо и того что есть (округление до 3 знаков)
      if (this.selectedPlacement) {
        this.quantity = this.suggestedQuantity(this.selectedPlacement.count)

        // Фокусируемся на инпуте количества
        this.$nextTick(() => {
          this.$refs.quantityInput?.focus()
          this.$refs.quantityInput?.select()
        })
      }
    },

    confirmPicking() {
      if (this.canConfirm) {
        const result = {
          quantity: this.quantity
        }

        // Для количественной с выбором места - передаем storehouse_product_id
        if (!this.product.is_single && this.selectedPlacement) {
          result.storehouse_product_id = this.selectedPlacement.storehouse_product_id
        }

        this.$emit('confirm', result)
      }
    },
    
    closeModal() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* Дополнительные стили при необходимости */
</style> 